import { Button } from '@/components/shadcn/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import { coerceRequestStatus, REQUEST_STATUS_CLOSED } from '@/pages/Subscriptions/Requests/types'
import { IconMenuOutline } from '@icons/IconMenuOutline'
import { useState } from 'react'
import { toast } from 'sonner'

type ExistingRequest = RestSchemas['Messages.Requests.ExistingRequest']

type PlatformTicketRequestRowActionsProps = {
  request: ExistingRequest
  onViewDetails: (request: ExistingRequest) => void
}

export function PlatformTicketRequestRowActions({
  request,
  onViewDetails,
}: PlatformTicketRequestRowActionsProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [confirmGrantOpen, setConfirmGrantOpen] = useState(false)
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false)
  const {
    postRequestsApiCloseRequest,
    postRequestsApiGrantRequest,
    getRequestsApiGetAdminRequests,
  } = useRestClient()

  const invalidateList = () => getRequestsApiGetAdminRequests.invalidateQueries(undefined)

  const { mutate: closeRequest, isPending: isClosing } = postRequestsApiCloseRequest.useMutation(undefined, {
    onSuccess: () => {
      invalidateList()
      toast.success('Request closed')
      setConfirmCloseOpen(false)
    },
    onError: () => toast.error('Failed to close request'),
  })

  const { mutate: grantRequest, isPending: isGranting } = postRequestsApiGrantRequest.useMutation(undefined, {
    onSuccess: () => {
      invalidateList()
      toast.success('Access granted and request closed')
      setConfirmGrantOpen(false)
    },
    onError: () => toast.error('Failed to grant request'),
  })

  const isOpen = coerceRequestStatus(request.status) !== REQUEST_STATUS_CLOSED
  const isPending = isClosing || isGranting

  const afterMenuCloses = (action: () => void) => {
    setMenuOpen(false)
    window.requestAnimationFrame(action)
  }

  const openDetail = () => afterMenuCloses(() => onViewDetails(request))
  const openGrantConfirm = () => afterMenuCloses(() => setConfirmGrantOpen(true))
  const openCloseConfirm = () => afterMenuCloses(() => setConfirmCloseOpen(true))

  return (
    <>
      {confirmGrantOpen && (
        <ConfirmDialog
          isOpen={confirmGrantOpen}
          onOpenChange={setConfirmGrantOpen}
          title="Grant requested access?"
          description="This will create the requested organisation, project, users, and application role assignments, then close the ticket."
          confirmLabel="Grant access"
          isSubmitting={isGranting}
          closeOnConfirm={false}
          onConfirm={() => grantRequest({ path: { requestId: request.id! } })}
        />
      )}

      {confirmCloseOpen && (
        <ConfirmDialog
          isOpen={confirmCloseOpen}
          onOpenChange={setConfirmCloseOpen}
          title="Close this request?"
          description="This will reject the request without granting access. The ticket record will be kept."
          confirmLabel="Close request"
          confirmVariant="destructive"
          isSubmitting={isClosing}
          closeOnConfirm={false}
          onConfirm={() => closeRequest({ path: { requestId: request.id! } })}
        />
      )}

      <DropdownMenu modal={false} open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" className="size-8">
            <IconMenuOutline size={16} />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={openDetail}>View details</DropdownMenuItem>
          {isOpen && (
            <>
              <DropdownMenuItem disabled={isPending} onSelect={openGrantConfirm}>
                Grant access
              </DropdownMenuItem>
              <DropdownMenuItem disabled={isPending} onSelect={openCloseConfirm}>
                Close request
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}
